/**
 * @file tier-discount.module.ts
 * @description Module definition for tier discount widgets.
 * Registers the module with the TAE framework and sets up block listeners.
 */

import { AppBlock } from '@boost/widget-integration-tae/lib';
import { Module, ModuleRefImpl } from 'framework/core';

import { DomHelper } from '../../helpers/dom.helper';
import { AppService } from '../app/app.service';
import { CartModule } from '../cart/cart.module';
import { PlatformModule } from '../platform/platform.module';
import { TemplateModule } from '../template/template.module';
import { TierDiscountAPI } from './tier-discount.api';
import { TierDiscountWidgetController } from './tier-discount.controller';
import { TierDiscountModel } from './tier-discount.model';
import { TierDiscountService } from './tier-discount.service';

/**
 * Module for tier discount bundle widgets.
 *
 * Integrates tier discount functionality into the TAE framework, providing:
 * - Automatic widget initialization from Shopify blocks
 * - Dependency injection for all services
 * - Lazy loading of widgets
 * - Extensibility for shop-specific customizations
 *
 * Technical Support teams can extend this module to customize initialization
 * or add shop-specific services to the dependency injection container.
 *
 * @example
 * Extend to add custom initialization logic:
 * ```typescript
 * @Module({
 *   imports: [TierDiscountModule],
 *   providers: [CustomTierDiscountService],
 * })
 * class CustomTierDiscountModule extends TierDiscountModule {
 *   protected setupBlockListener() {
 *     this.appService.TAEApp.addRegistryBlockListener((block) => {
 *       if (block.blockType === 'tierDiscount' || block.blockType === 'custom-tier') {
 *         this.onInitBlock(block);
 *       }
 *     });
 *   }
 * }
 * ```
 */
@Module({
  imports: [TemplateModule, CartModule, PlatformModule],
  providers: [DomHelper, TierDiscountAPI, TierDiscountService],
  exports: [TierDiscountModel, TierDiscountWidgetController],
})
export class TierDiscountModule {
  // Static reference to module instance for search page helper
  private static instance: TierDiscountModule;

  constructor(
    protected moduleRef: ModuleRefImpl,
    protected appService: AppService
  ) {
    // Store instance for static access
    TierDiscountModule.instance = this;
    this.setupBlockListener();

    // Import search page helper to register the reinitialize event listener
    import('./tier-discount-search.helper').catch(() => {
      // Silently fail if search helper is not available
    });
  }

  /**
   * Initializes tier discount widget on search page
   * Called from tier-discount-search.helper.ts
   * @param container - DOM element to render widget into
   */
  static async initializeForSearchPage(container: HTMLElement): Promise<void> {
    if (!TierDiscountModule.instance) {
      return;
    }

    try {
      const widget: TierDiscountWidgetController =
        await TierDiscountModule.instance.moduleRef.resolve(TierDiscountWidgetController);

      widget.connect(
        {
          widgetId: 'bc-tier-discount-bundle',
        },
        container
      );
    } catch (error) {
      console.error('[TierDiscountModule] Error initializing widget:', error);
      throw error;
    }
  }

  /**
   * Sets up the TAE App block listener for tier discount widgets.
   * Override this method to customize block registration logic or support additional block types.
   *
   * @example
   * // Add support for custom block types
   * protected setupBlockListener() {
   *   this.appService.TAEApp.addRegistryBlockListener((block) => {
   *     if (block.blockType === 'tierDiscount' || block.blockType === 'vip-tier-discount') {
   *       this.onInitBlock(block);
   *     }
   *   });
   * }
   */
  protected setupBlockListener(): void {
    this.appService.TAEApp.addRegistryBlockListener((block) => {
      if (block.blockType === 'tierDiscountBundle') {
        this.onInitBlock(block);
      }
    });
  }

  /**
   * Called when a tier discount block is initialized.
   * Override this method to customize widget initialization or add shop-specific setup logic.
   *
   * @param block - The AppBlock instance being initialized
   * @returns Promise that resolves when the block initialization is complete
   *
   * @example
   * // Add shop-specific initialization
   * protected async onInitBlock(block: AppBlock) {
   *   // Add custom tracking before widget initialization
   *   this.trackWidgetInit(block.id);
   *   await super.onInitBlock(block);
   * }
   */
  protected async onInitBlock(block: AppBlock): Promise<void> {
    try {
      if (block.document instanceof HTMLElement) {
        const widget: TierDiscountWidgetController = await this.moduleRef.resolve(
          TierDiscountWidgetController
        );

        await this.connectWidget(widget, block);
      }
    } catch (error) {
      this.handleBlockInitError(error as Error, block);
    }
  }

  /**
   * Connects the widget controller to the DOM element.
   * Override this method to customize widget connection parameters or add pre-connection logic.
   *
   * @param widget - The widget controller instance
   * @param block - The AppBlock instance
   * @returns Promise that resolves when the widget connection is complete
   *
   * @example
   * // Add custom widget connection parameters
   * protected async connectWidget(widget, block) {
   *   const customParams = {
   *     widgetId: this.parseWidgetId(block.id),
   *     shopTheme: this.getShopTheme(),
   *   };
   *   widget.connect(customParams, block.document);
   * }
   */
  protected async connectWidget(
    widget: TierDiscountWidgetController,
    block: AppBlock
  ): Promise<void> {
    widget.connect(
      {
        widgetId: this.parseWidgetId(block.id),
      },
      block.document as HTMLElement
    );
  }

  /**
   * Parses the widget ID from the block ID.
   * Override this method to customize widget ID extraction logic for different shops or naming conventions.
   *
   * @param blockId - The raw block ID from TAE
   * @returns The parsed widget ID
   *
   * @example
   * // Custom widget ID parsing for shop with different prefix
   * protected parseWidgetId(blockId: string): string {
   *   return blockId.replace('custom-widget-prefix-', '');
   * }
   */
  protected parseWidgetId(blockId: string): string {
    return blockId.replace('boost-sd-widget-', '');
  }

  /**
   * Handles errors during block initialization.
   * Override this method to customize error handling, reporting, or recovery logic.
   *
   * @remarks
   * The default implementation logs errors to the console. Technical Support teams
   * can extend this to integrate with shop-specific error tracking services
   * (e.g., Sentry, Bugsnag) or implement custom fallback behavior.
   *
   * @param error - The error that occurred during initialization
   * @param block - The AppBlock that failed to initialize
   *
   * @example
   * // Custom error reporting to shop's monitoring system
   * protected handleBlockInitError(error: Error, block: AppBlock): void {
   *   this.sendToShopMonitoring({
   *     type: 'widget_init_error',
   *     widgetId: block.id,
   *     error: error.message,
   *   });
   *   super.handleBlockInitError(error, block);
   * }
   */
  protected handleBlockInitError(error: Error, block: AppBlock): void {
    console.error(`Failed to initialize tier discount widget for block ${block.id}:`, error);
  }
}
